<template>
    <div class="date-range-bar" :aria-busy="busy" :class="{ dark: isDark }">
        <div class="date-range-left">
            <button
                v-for="range in ranges"
                :key="range.key"
                class="range-btn"
                :disabled="busy"
                :class="{ active: activeRange === range.key }"
                @click="selectRange(range.key)"
            >{{ range.label }}</button>
            <button class="range-btn range-custom-btn" :disabled="busy" :class="{ active: activeRange === 'custom' }" @click="toggleCustomPicker" title="Custom date range">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
            </button>
        </div>
        <div class="date-range-right">
            <span class="timezone-clock">{{ currentTime }}</span>
            <div class="timezone-selector-wrapper">
                <button class="timezone-btn" @click="toggleTimezoneDropdown">
                    {{ selectedTimezoneLabel }}
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="6 9 12 15 18 9"/>
                    </svg>
                </button>
                <transition name="tz-dropdown-fade">
                    <div v-if="showTimezoneDropdown" class="timezone-dropdown">
                        <input
                            ref="tzSearch"
                            v-model="tzSearchQuery"
                            class="tz-search-input"
                            placeholder="Search timezone..."
                            @keydown.esc="showTimezoneDropdown = false"
                        />
                        <div class="tz-list">
                            <div
                                v-for="tz in filteredTimezones"
                                :key="tz.value + tz.label"
                                class="tz-item"
                                :class="{ active: selectedTimezone === tz.value }"
                                @click="selectTimezone(tz)"
                            >
                                <span class="tz-label">{{ tz.label }}</span>
                                <span class="tz-offset">{{ tz.offsetLabel }}</span>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DateRangeBar',
    props: {
        busy: { type: Boolean, default: false },
        isDark: {
            type: Boolean,
            default: false
        },
        timezone: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            activeRange: null,
            showTimezoneDropdown: false,
            tzSearchQuery: '',
            currentTime: '',
            selectedTimezone: null,
            clockInterval: null,
            ranges: [
                { key: '1D', label: '1D' },
                { key: '5D', label: '5D' },
                { key: '1M', label: '1M' },
                { key: '3M', label: '3M' },
                { key: '6M', label: '6M' },
                { key: 'YTD', label: 'YTD' },
                { key: '1Y', label: '1Y' },
                { key: '5Y', label: '5Y' },
                { key: 'All', label: 'All' }
            ],
            timezones: [
                { label: 'UTC', value: 0, offsetLabel: 'UTC+0' },
                { label: 'London', value: 0, offsetLabel: 'UTC+0' },
                { label: 'Paris', value: 1, offsetLabel: 'UTC+1' },
                { label: 'Berlin', value: 1, offsetLabel: 'UTC+1' },
                { label: 'Cairo', value: 2, offsetLabel: 'UTC+2' },
                { label: 'Istanbul', value: 3, offsetLabel: 'UTC+3' },
                { label: 'Moscow', value: 3, offsetLabel: 'UTC+3' },
                { label: 'Dubai', value: 4, offsetLabel: 'UTC+4' },
                { label: 'Mumbai (IST)', value: 5.5, offsetLabel: 'UTC+5:30' },
                { label: 'Kolkata (IST)', value: 5.5, offsetLabel: 'UTC+5:30' },
                { label: 'Dhaka', value: 6, offsetLabel: 'UTC+6' },
                { label: 'Bangkok', value: 7, offsetLabel: 'UTC+7' },
                { label: 'Singapore', value: 8, offsetLabel: 'UTC+8' },
                { label: 'Hong Kong', value: 8, offsetLabel: 'UTC+8' },
                { label: 'Shanghai', value: 8, offsetLabel: 'UTC+8' },
                { label: 'Tokyo', value: 9, offsetLabel: 'UTC+9' },
                { label: 'Seoul', value: 9, offsetLabel: 'UTC+9' },
                { label: 'Sydney', value: 10, offsetLabel: 'UTC+10' },
                { label: 'Auckland', value: 12, offsetLabel: 'UTC+12' },
                { label: 'Hawaii', value: -10, offsetLabel: 'UTC-10' },
                { label: 'Alaska', value: -9, offsetLabel: 'UTC-9' },
                { label: 'Los Angeles (PT)', value: -8, offsetLabel: 'UTC-8' },
                { label: 'Denver (MT)', value: -7, offsetLabel: 'UTC-7' },
                { label: 'Chicago (CT)', value: -6, offsetLabel: 'UTC-6' },
                { label: 'New York (ET)', value: -5, offsetLabel: 'UTC-5' },
                { label: 'São Paulo', value: -3, offsetLabel: 'UTC-3' }
            ]
        }
    },
    computed: {
        selectedTimezoneLabel() {
            if (this.selectedTimezone === null) return 'UTC'
            const tz = this.timezones.find(t => t.value === this.selectedTimezone)
            if (tz) return tz.offsetLabel
            const sign = this.selectedTimezone >= 0 ? '+' : ''
            const hrs = Math.floor(Math.abs(this.selectedTimezone))
            const mins = (Math.abs(this.selectedTimezone) % 1) * 60
            if (mins > 0) return 'UTC' + sign + (this.selectedTimezone >= 0 ? '' : '-') + hrs + ':' + (mins < 10 ? '0' : '') + Math.round(mins)
            return 'UTC' + sign + this.selectedTimezone
        },
        filteredTimezones() {
            if (!this.tzSearchQuery) return this.timezones
            const q = this.tzSearchQuery.toLowerCase()
            return this.timezones.filter(tz =>
                tz.label.toLowerCase().includes(q) ||
                tz.offsetLabel.toLowerCase().includes(q)
            )
        }
    },
    watch: {
        timezone: {
            immediate: true,
            handler(val) {
                this.selectedTimezone = val
            }
        },
        selectedTimezone() {
            this.updateClock()
        },
        showTimezoneDropdown(val) {
            if (val) {
                this.$nextTick(() => {
                    if (this.$refs.tzSearch) this.$refs.tzSearch.focus()
                })
                document.addEventListener('click', this.handleOutsideClick)
            } else {
                this.tzSearchQuery = ''
                document.removeEventListener('click', this.handleOutsideClick)
            }
        }
    },
    mounted() {
        this.detectBrowserTimezone()
        this.updateClock()
        this.clockInterval = setInterval(this.updateClock, 1000)
    },
    beforeDestroy() {
        if (this.clockInterval) clearInterval(this.clockInterval)
        document.removeEventListener('click', this.handleOutsideClick)
    },
    methods: {
        detectBrowserTimezone() {
            const offsetMinutes = -new Date().getTimezoneOffset()
            const offsetHours = offsetMinutes / 60
            const match = this.timezones.find(tz => tz.value === offsetHours)
            if (match) {
                this.selectedTimezone = match.value
            } else {
                this.selectedTimezone = offsetHours
            }
            this.$emit('timezone-change', this.selectedTimezone)
        },
        updateClock() {
            const now = new Date()
            const offset = (this.selectedTimezone || 0) * 60
            const utc = now.getTime() + now.getTimezoneOffset() * 60000
            const tzTime = new Date(utc + offset * 60000)
            const h = String(tzTime.getHours()).padStart(2, '0')
            const m = String(tzTime.getMinutes()).padStart(2, '0')
            const s = String(tzTime.getSeconds()).padStart(2, '0')
            this.currentTime = h + ':' + m + ':' + s
        },
        selectRange(key) {
            this.activeRange = key
            this.$emit('range-select', key)
        },
        toggleCustomPicker() {
            this.activeRange = 'custom'
            this.$emit('custom-range')
        },
        toggleTimezoneDropdown() {
            this.showTimezoneDropdown = !this.showTimezoneDropdown
        },
        selectTimezone(tz) {
            this.selectedTimezone = tz.value
            this.showTimezoneDropdown = false
            this.$emit('timezone-change', tz.value)
        },
        handleOutsideClick(e) {
            const wrapper = this.$el.querySelector('.timezone-selector-wrapper')
            if (wrapper && !wrapper.contains(e.target)) {
                this.showTimezoneDropdown = false
            }
        }
    }
}
</script>

<style scoped>
.date-range-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 32px;
    padding: 0 12px;
    border-top: 1px solid #e0e3eb;
    background: #ffffff;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    user-select: none;
    flex-shrink: 0;
    position: relative;
    z-index: 40;
}
.date-range-bar.dark {
    background: rgb(18, 24, 38);
    border-top-color: rgba(55, 59, 75, 0.6);
}

.date-range-left {
    display: flex;
    align-items: center;
    gap: 2px;
}

.range-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    min-width: 24px;
    padding: 0 6px;
    font-size: 11px;
    font-weight: 500;
    color: #787b86;
    background: transparent;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    transition: color 0.12s ease, background-color 0.12s ease;
    font-family: inherit;
}
.range-btn:disabled { opacity: .5; cursor: progress; }
.range-btn:hover {
    color: #131722;
    background: rgba(0, 0, 0, 0.04);
}
.range-btn.active {
    color: #2962ff;
    background: rgba(41, 98, 255, 0.08);
    font-weight: 600;
}
.dark .range-btn {
    color: #9ca3af;
}
.dark .range-btn:hover {
    color: #d1d4dc;
    background: rgba(255, 255, 255, 0.06);
}
.dark .range-btn.active {
    color: #5b9cf6;
    background: rgba(91, 156, 246, 0.12);
}

.range-custom-btn {
    padding: 0 5px;
}

.date-range-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.timezone-clock {
    font-size: 11px;
    font-weight: 500;
    color: #131722;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    letter-spacing: 0.02em;
}
.dark .timezone-clock {
    color: #d1d4dc;
}

.timezone-selector-wrapper {
    position: relative;
}

.timezone-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 24px;
    padding: 0 8px;
    font-size: 11px;
    font-weight: 500;
    color: #787b86;
    background: transparent;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    transition: color 0.12s ease, background-color 0.12s ease;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.timezone-btn:hover {
    color: #131722;
    background: rgba(0, 0, 0, 0.04);
}
.dark .timezone-btn {
    color: #9ca3af;
}
.dark .timezone-btn:hover {
    color: #d1d4dc;
    background: rgba(255, 255, 255, 0.06);
}

.timezone-dropdown {
    position: absolute;
    bottom: calc(100% + 6px);
    right: 0;
    width: 220px;
    max-height: 300px;
    background: #ffffff;
    border: 1px solid #e0e3eb;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    z-index: 9999;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}
.dark .timezone-dropdown {
    background: #1a2035;
    border-color: rgba(55, 59, 75, 0.6);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

.tz-search-input {
    width: 100%;
    padding: 8px 10px;
    font-size: 12px;
    border: none;
    border-bottom: 1px solid #e0e3eb;
    outline: none;
    background: transparent;
    color: #131722;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    box-sizing: border-box;
}
.tz-search-input::placeholder {
    color: #9ca3af;
}
.dark .tz-search-input {
    color: #d1d4dc;
    border-bottom-color: rgba(55, 59, 75, 0.6);
}
.dark .tz-search-input::placeholder {
    color: #6b7280;
}

.tz-list {
    overflow-y: auto;
    max-height: 260px;
    padding: 4px 0;
}

.tz-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 10px;
    cursor: pointer;
    transition: background-color 0.1s ease;
}
.tz-item:hover {
    background: #f0f3fa;
}
.tz-item.active {
    background: rgba(41, 98, 255, 0.08);
}
.dark .tz-item:hover {
    background: rgba(255, 255, 255, 0.05);
}
.dark .tz-item.active {
    background: rgba(91, 156, 246, 0.12);
}

.tz-label {
    font-size: 12px;
    font-weight: 500;
    color: #131722;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.dark .tz-label {
    color: #d1d4dc;
}

.tz-offset {
    font-size: 11px;
    color: #787b86;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.dark .tz-offset {
    color: #6b7280;
}

.tz-dropdown-fade-enter-active,
.tz-dropdown-fade-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}
.tz-dropdown-fade-enter,
.tz-dropdown-fade-leave-to {
    opacity: 0;
    transform: translateY(4px);
}
</style>
